import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router';

import connectionLine from '@fly/assets/images/connection_line.svg';
import connectionLineDark from '@fly/assets/images/connection_line_dark.svg';
import connectionLineHalfLeft from '@fly/assets/images/connection_line_half_left.svg';
import connectionLineHalfLeftDark from '@fly/assets/images/connection_line_half_left_dark.svg';
import connectionLineHalfRight from '@fly/assets/images/connection_line_half_right.svg';
import connectionLineHalfRightDark from '@fly/assets/images/connection_line_half_right_dark.svg';
import flyLogoMono from '@fly/assets/images/fly_logo_mono.svg';
import flyLogoMonoDark from '@fly/assets/images/fly_logo_mono_dark.svg';
import githubLogo from '@fly/assets/images/github_logo.svg';
import githubLogoDark from '@fly/assets/images/github_logo_dark.svg';
import { Heading, Text } from '@fly/core';
import {
  ALLOWED_RETURN_PROTOCOLS,
  GITHUB_CODING_AGENT_CONTEXT_STORAGE_KEY,
  GITHUB_QUERY_PARAMS,
  routes,
} from '@fly/shared';
import { FlyGithubInstallButton } from '@fly/shared-ui';
import { useGetGithubInstallationQuery } from '@fly/store';
import { FlySvgSprite } from '@fly/svg';

import styles from './GitHubCallback.module.less';

const CallbackStatus = {
  INSTALLATION_SUCCESS: 'installation_success',
  UPDATE_SUCCESS: 'update_success',
  PENDING_APPROVAL: 'pending_approval',
  UNEXPECTED_ERROR: 'unexpected_error',
  ORG_CONNECTED_TO_ANOTHER_ACCOUNT: 'org_connected_to_another_account',
} as const;

type CallbackStatus = (typeof CallbackStatus)[keyof typeof CallbackStatus];

const AUTO_OPEN_DELAY_MS = 1500;

interface StatusConfig {
  headingKey: string;
  headingDefault: string;
  messageKey: string;
  messageDefault: string;
}

function isSuccessStatus(status: CallbackStatus | null): boolean {
  return (
    status === CallbackStatus.INSTALLATION_SUCCESS ||
    status === CallbackStatus.UPDATE_SUCCESS
  );
}

function getStatusConfig(status: CallbackStatus | null): StatusConfig {
  switch (status) {
    case CallbackStatus.INSTALLATION_SUCCESS:
      return {
        headingKey: 'app.auth.github_callback.success_heading',
        headingDefault: 'Connected successfully',
        messageKey: 'app.auth.github_callback.success_message',
        messageDefault: 'GitHub is now connected to Fly.',
      };
    case CallbackStatus.UPDATE_SUCCESS:
      return {
        headingKey: 'app.auth.github_callback.update_heading',
        headingDefault: 'Updated successfully',
        messageKey: 'app.auth.github_callback.update_message',
        messageDefault: 'Repository access has been updated.',
      };
    case CallbackStatus.PENDING_APPROVAL:
      return {
        headingKey: 'app.auth.github_callback.pending_heading',
        headingDefault: 'Pending approval',
        messageKey: 'app.auth.github_callback.pending_message',
        messageDefault:
          'Your GitHub App installation request has been sent to the organization admin for approval.',
      };
    case CallbackStatus.ORG_CONNECTED_TO_ANOTHER_ACCOUNT:
      return {
        headingKey:
          'app.auth.github_callback.org_connected_to_another_account_heading',
        headingDefault: 'Unable to connect',
        messageKey:
          'app.auth.github_callback.org_connected_to_another_account_message',
        messageDefault:
          'Seems that this GitHub organization is already being used with another Fly account. Disconnect it from the existing account or select another organization.',
      };
    case CallbackStatus.UNEXPECTED_ERROR:
      return {
        headingKey: 'app.auth.github_callback.error_heading',
        headingDefault: 'Something went wrong',
        messageKey: 'app.auth.github_callback.error_message',
        messageDefault:
          'An error occurred during GitHub setup. Please try again.',
      };
    default:
      return {
        headingKey: 'app.auth.github_callback.success_heading',
        headingDefault: 'Connected successfully',
        messageKey: 'app.auth.github_callback.success_message',
        messageDefault: 'GitHub is now connected to Fly.',
      };
  }
}

// Default connection line (a dot at each end) shown between the logos for the
// normal callback states. Light/dark image variants follow the existing pattern.
function ConnectionLine() {
  return (
    <>
      <img
        className={`${styles.connectionLine} block dark:hidden`}
        src={connectionLine}
        alt=''
        aria-hidden='true'
      />
      <img
        className={`${styles.connectionLine} hidden dark:block`}
        src={connectionLineDark}
        alt=''
        aria-hidden='true'
      />
    </>
  );
}

// Half connection line used on either side of the warning icon. It keeps the
// dot only on the outer end (toward the logo) and omits the inner dot so the
// line runs cleanly into the warning icon. side='left' -> dot on the left,
// side='right' -> dot on the right. Light/dark image variants follow the same
// pattern as ConnectionLine.
function ConnectionLineHalf({ side }: { side: 'left' | 'right' }) {
  const light =
    side === 'left' ? connectionLineHalfLeft : connectionLineHalfRight;
  const dark =
    side === 'left' ? connectionLineHalfLeftDark : connectionLineHalfRightDark;
  return (
    <>
      <img
        className='w-5 block h-[5px] dark:hidden'
        src={light}
        alt=''
        aria-hidden='true'
      />
      <img
        className='w-5 hidden h-[5px] dark:block'
        src={dark}
        alt=''
        aria-hidden='true'
      />
    </>
  );
}

export function GitHubCallback() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const protocolTriggered = useRef(false);

  const status = searchParams.get('status') as CallbackStatus | null;
  const isOrgConnectedError =
    status === CallbackStatus.ORG_CONNECTED_TO_ANOTHER_ACCOUNT;

  // Only fetch the install URL when we need to render the "select another org"
  // CTA; every other callback state skips the request.
  const { data: githubConfig } = useGetGithubInstallationQuery(undefined, {
    skip: !isOrgConnectedError,
  });
  // The CTA must point at the GitHub App install/select-org page
  // (.../installations/new?state=...). No fallback to a bare github.com URL —
  // that is a dead end. The button only renders once this resolves.
  const installationUrl = githubConfig?.appInstallationUrl || '';

  const codingAgentFromUrl = searchParams.get(GITHUB_QUERY_PARAMS.CODING_AGENT);
  const returnProtocolFromUrl = searchParams.get(
    GITHUB_QUERY_PARAMS.RETURN_PROTOCOL
  );

  // Read coding agent context from sessionStorage once at mount. Using a useState initializer
  // ensures the read+remove runs exactly once even in React Strict Mode (where the
  // component function body executes twice per render).
  const [codingAgentContext] = useState<Record<string, string> | null>(() => {
    if (codingAgentFromUrl || returnProtocolFromUrl) return null;
    try {
      const stored = sessionStorage.getItem(
        GITHUB_CODING_AGENT_CONTEXT_STORAGE_KEY
      );
      if (stored) {
        sessionStorage.removeItem(GITHUB_CODING_AGENT_CONTEXT_STORAGE_KEY);
        return JSON.parse(stored) as Record<string, string>;
      }
    } catch {
      // ignore malformed sessionStorage data
    }
    return null;
  });

  const codingAgent =
    codingAgentFromUrl ??
    codingAgentContext?.[GITHUB_QUERY_PARAMS.CODING_AGENT] ??
    null;
  const returnProtocol =
    returnProtocolFromUrl ??
    codingAgentContext?.[GITHUB_QUERY_PARAMS.RETURN_PROTOCOL] ??
    null;

  // When update_success arrives without any coding agent context (neither URL params
  // nor sessionStorage), the user updated repos outside of a coding agent flow
  // (e.g. directly from GitHub settings). Redirect to the app instead of
  // showing a dead-end callback page.
  const shouldRedirectToApp =
    status === CallbackStatus.UPDATE_SUCCESS && !codingAgent && !returnProtocol;

  useEffect(() => {
    if (shouldRedirectToApp) {
      navigate(`${routes.DASHBOARD.ROOT}?status=${status}`, { replace: true });
    }
  }, [shouldRedirectToApp, status, navigate]);

  const config = getStatusConfig(status);

  const heading = t(config.headingKey, { defaultValue: config.headingDefault });
  const message = t(config.messageKey, { defaultValue: config.messageDefault });
  const returnMessage = codingAgent
    ? t('app.auth.github_callback.return_to_agent', {
        defaultValue: `You can return to ${codingAgent}.`,
        agent: codingAgent,
      })
    : t('app.auth.github_callback.return_to_generic', {
        defaultValue: 'You can return to your coding agent.',
      });

  useEffect(() => {
    if (
      protocolTriggered.current ||
      !isSuccessStatus(status) ||
      !returnProtocol ||
      !ALLOWED_RETURN_PROTOCOLS.has(returnProtocol)
    ) {
      return;
    }

    const timer = setTimeout(() => {
      protocolTriggered.current = true;
      window.location.href = `${returnProtocol}://`;
    }, AUTO_OPEN_DELAY_MS);

    return () => clearTimeout(timer);
  }, [status, returnProtocol]);

  if (shouldRedirectToApp) {
    return null;
  }

  return (
    <div className={styles.page} data-testid='github-callback-page'>
      <div className={styles.content}>
        <div className={styles.connectionIcons}>
          <img
            className={`${styles.flyLogo} block dark:hidden`}
            src={flyLogoMono}
            alt='JFrog Fly'
            data-testid='fly-logo'
          />
          <img
            className={`${styles.flyLogo} hidden dark:block`}
            src={flyLogoMonoDark}
            alt='JFrog Fly'
          />
          {isOrgConnectedError ? (
            <>
              <ConnectionLineHalf side='left' />
              <FlySvgSprite
                className='text-gray-9'
                name='warning'
                size={24}
                data-testid='callback-warning-icon'
              />
              <ConnectionLineHalf side='right' />
            </>
          ) : (
            <ConnectionLine />
          )}
          <img
            className={`${styles.githubLogo} block dark:hidden`}
            src={githubLogo}
            alt='GitHub'
            data-testid='github-logo'
          />
          <img
            className={`${styles.githubLogo} hidden dark:block`}
            src={githubLogoDark}
            alt='GitHub'
          />
        </div>
        <div className={styles.textGroup}>
          <Heading
            as='h1'
            size='8'
            weight='medium'
            data-testid='callback-heading'
          >
            {heading}
          </Heading>
          <Text size='5' data-testid='callback-message'>
            {message}
          </Text>
          {!isOrgConnectedError && (
            <Text size='3' color='gray' data-testid='callback-return'>
              {returnMessage}
            </Text>
          )}
        </div>
        {isOrgConnectedError && installationUrl && (
          <FlyGithubInstallButton
            installationUrl={installationUrl}
            text={t(
              'app.auth.github_callback.org_connected_to_another_account_cta',
              { defaultValue: 'Select alternative organization' }
            )}
          />
        )}
      </div>
    </div>
  );
}
